import { Dialog as NavigationDialog, DialogPanel as NavigationPanel, DialogTitle as NavigationTitle } from '@headlessui/react';
import React, { useState, useEffect, useRef } from 'react';
import { ThemeSelect } from '../design-system/ThemeSelect';
import { useTheme } from '../design-system/ThemeProvider';
import BrandLogo from './BrandLogo';
import { FolderOpen, User as UserIcon, LogOut, LogIn, Crown, Scale, BookOpen, ChevronDown, Menu, X, Bell, FileText } from 'lucide-react';
import { useDosar } from '../context/DosarContext.shared';
import { useAuth } from '../context/AuthContext.shared';
import { showPremiumOffer } from '../lib/subscriptionAccess';
import { useLanguage } from '../context/LanguageContext.shared';
import { Link, useLocation } from 'react-router-dom';
import { usePublicSettings } from '../context/PublicSettingsContext.shared';
import LegislativeTicker from './LegislativeTicker';
import DesktopGuestAccountMenu from './DesktopGuestAccountMenu';

interface DosarButtonProps {
  customClass?: string;
  onNavigate?: () => void;
  labelClass?: string;
}

const DosarButton: React.FC<DosarButtonProps> = ({ customClass, onNavigate, labelClass }) => {
  const { toggleDrawer, items } = useDosar();
  const { t } = useLanguage();
  return (
    <button
      type="button"
      aria-label={t('common.cases')}
      title={t('common.cases')}
      onClick={() => { toggleDrawer(); onNavigate?.(); }}
      className={`relative group flex items-center gap-2 text-sm font-medium transition-all duration-300 ${customClass || 'text-content-secondary hover:text-accent'}`}
    >
      <FolderOpen size={18} strokeWidth={1.8} />
      <span className={labelClass ?? (onNavigate ? "text-sm font-medium" : customClass?.includes('flex-col') ? "text-[11px] sm:text-[9px] font-medium" : "hidden md:inline")}>{t('common.cases')}</span>
      {items.length > 0 && (
        <span className="bg-danger text-background text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center shadow-subtle leading-none shrink-0">
          {items.length}
        </span>
      )}
    </button>
  );
};

interface HeaderProps {
  onContribuieClick?: () => void;
  isHomeView?: boolean;
  onReset?: () => void;
  theme?: 'light' | 'dark';
}

type DesktopMenuId = 'exams' | 'jurisprudence' | 'useful' | null;

const UserTypeBadge: React.FC<{ role?: string; isDark?: boolean; isMobile?: boolean }> = ({ role, isDark, isMobile }) => {
  const roleUpper = role?.toUpperCase() || 'BASIC';
  const displayRole = roleUpper === 'ADMIN' ? 'ADMIN' : (roleUpper === 'PRO' || roleUpper === 'PREMIUM' ? 'PRO' : 'BASIC');

  if (displayRole === 'BASIC') return null;

  const baseClasses = `absolute left-1/2 -translate-x-1/2 flex items-center justify-center font-extrabold tracking-widest rounded-full shadow-subtle z-10 border-[1.5px] ${isDark ? 'border-border' : 'border-border'
    } ${isMobile ? '-bottom-2 px-1.5 py-[1px] text-[7px] leading-none' : '-bottom-2.5 px-2 py-0.5 text-[8px] leading-none'
    }`;

  let colorClasses = "";
  if (displayRole === 'PRO') {
    colorClasses = "bg-warning text-background";
  } else if (displayRole === 'ADMIN') {
    colorClasses = "bg-surface-subtle text-content";
  }

  return (
    <div className={`${baseClasses} ${colorClasses}`}>
      {displayRole}
    </div>
  );
};

const FlagIcon: React.FC<{ code: string; className?: string }> = ({ code, className = "w-full h-full object-cover" }) => {
  if (code === 'ro') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2" className={className}>
        <rect width="1" height="2" fill="#002B7F" />
        <rect x="1" width="1" height="2" fill="#FCD116" />
        <rect x="2" width="1" height="2" fill="#CE1126" />
      </svg>
    );
  }
  if (code === 'en') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" className={className}>
        <rect width="60" height="30" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="2" />
        <path d="M0,15 H60 M30,0 V30" stroke="#fff" strokeWidth="10" />
        <path d="M0,15 H60 M30,0 V30" stroke="#C8102E" strokeWidth="6" />
      </svg>
    );
  }
  if (code === 'fr') {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 2" className={className}>
        <rect width="1" height="2" fill="#002395" />
        <rect x="1" width="1" height="2" fill="#FFFFFF" />
        <rect x="2" width="1" height="2" fill="#ED2939" />
      </svg>
    );
  }
  return null;
};

const Header: React.FC<HeaderProps> = ({ onContribuieClick, isHomeView = false, onReset }) => {
  const { pathname } = useLocation();
  const { user, isAuthenticated, logout, isLoading: authLoading } = useAuth();
  const showPremium = showPremiumOffer(user, authLoading);
  const { toggleDrawer, items: dosarItems } = useDosar();
  const { language, setLanguage, t } = useLanguage();

  const editorDocPath =
    language === 'en'
      ? '/en/editor-document'
      : language === 'fr'
      ? '/fr/editor-document'
      : '/editor-document';

  const isEditorDocActive =
    location.pathname.startsWith('/editor-document') ||
    location.pathname.startsWith('/en/editor-document') ||
    location.pathname.startsWith('/fr/editor-document') ||
    location.pathname.startsWith('/document-editor');
  const { settings } = usePublicSettings();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showGuestAccountMenu, setShowGuestAccountMenu] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [showLangDropdownMobile, setShowLangDropdownMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileExamsOpen, setMobileExamsOpen] = useState(false);
  const [mobileUsefulOpen, setMobileUsefulOpen] = useState(false);
  const [desktopMenu, setDesktopMenu] = useState<DesktopMenuId>(null);
  const [pinnedDesktopMenu, setPinnedDesktopMenu] = useState<DesktopMenuId>(null);

  // The mobile drawer releases focus/scroll ownership when its layout disappears.
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => { if (desktop.matches) setShowMobileMenu(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  // Click-outside: closes menus when clicking outside
  const profileMobileRef = useRef<HTMLDivElement>(null);
  const profileDesktopRef = useRef<HTMLDivElement>(null);
  const guestAccountButtonRef = useRef<HTMLButtonElement>(null);
  const guestAccountMenuRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const languageMenuMobileRef = useRef<HTMLDivElement>(null);
  const desktopMenusRef = useRef<HTMLDivElement>(null);
  const examsButtonRef = useRef<HTMLButtonElement>(null);
  const jurisprudenceButtonRef = useRef<HTMLButtonElement>(null);
  const usefulButtonRef = useRef<HTMLButtonElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const examsMenuRef = useRef<HTMLDivElement>(null);
  const jurisprudenceMenuRef = useRef<HTMLDivElement>(null);
  const usefulMenuRef = useRef<HTMLDivElement>(null);
  const desktopMenuCloseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        profileMobileRef.current && !profileMobileRef.current.contains(target) &&
        profileDesktopRef.current && !profileDesktopRef.current.contains(target)
      ) {
        setShowProfileMenu(false);
      }
      if (
        guestAccountMenuRef.current && !guestAccountMenuRef.current.contains(target) &&
        guestAccountButtonRef.current && !guestAccountButtonRef.current.contains(target)
      ) {
        setShowGuestAccountMenu(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(target)) {
        setShowLangDropdown(false);
      }
      if (languageMenuMobileRef.current && !languageMenuMobileRef.current.contains(target)) {
        setShowLangDropdownMobile(false);
      }
      if (desktopMenusRef.current && !desktopMenusRef.current.contains(target)) {
        setDesktopMenu(null);
        setPinnedDesktopMenu(null);
      }
    };
    const handleFocusOutside = (e: FocusEvent) => {
      const target = e.target as Node;
      if (
        profileMobileRef.current && !profileMobileRef.current.contains(target) &&
        profileDesktopRef.current && !profileDesktopRef.current.contains(target)
      ) {
        setShowProfileMenu(false);
      }
      if (
        guestAccountMenuRef.current && !guestAccountMenuRef.current.contains(target) &&
        guestAccountButtonRef.current && !guestAccountButtonRef.current.contains(target)
      ) {
        setShowGuestAccountMenu(false);
      }
      if (languageMenuRef.current && !languageMenuRef.current.contains(target)) {
        setShowLangDropdown(false);
      }
      if (languageMenuMobileRef.current && !languageMenuMobileRef.current.contains(target)) {
        setShowLangDropdownMobile(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleFocusOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleFocusOutside);
    };
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      const activeDesktopMenu = desktopMenu;
      const focusTarget = activeDesktopMenu
        ? (activeDesktopMenu === 'exams' ? examsButtonRef : activeDesktopMenu === 'jurisprudence' ? jurisprudenceButtonRef : usefulButtonRef)
        : showProfileMenu
          ? profileButtonRef
          : showLangDropdown
            ? languageButtonRef
            : showGuestAccountMenu
              ? guestAccountButtonRef
              : null;
      setDesktopMenu(null);
      setPinnedDesktopMenu(null);
      setShowProfileMenu(false);
      setShowGuestAccountMenu(false);
      setShowLangDropdown(false);
      setShowLangDropdownMobile(false);
      setShowMobileMenu(false);

      if (focusTarget) {
        window.requestAnimationFrame(() => {
          focusTarget.current?.focus();
        });
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [desktopMenu, showLangDropdown, showProfileMenu, showGuestAccountMenu]);

  useEffect(() => {
    setDesktopMenu(null);
    setPinnedDesktopMenu(null);
    setShowProfileMenu(false);
    setShowGuestAccountMenu(false);
    setShowLangDropdown(false);
    setShowLangDropdownMobile(false);
    setShowMobileMenu(false);
  }, [pathname]);

  useEffect(() => {
    const closeDesktopMenuBelowBreakpoint = () => {
      if (window.innerWidth < 768) {
        setDesktopMenu(null);
        setPinnedDesktopMenu(null);
        setShowGuestAccountMenu(false);
      }
    };

    window.addEventListener('resize', closeDesktopMenuBelowBreakpoint);
    return () => window.removeEventListener('resize', closeDesktopMenuBelowBreakpoint);
  }, []);

  useEffect(() => () => {
    if (desktopMenuCloseTimerRef.current !== null) {
      window.clearTimeout(desktopMenuCloseTimerRef.current);
    }
  }, []);

  const cancelScheduledDesktopMenuClose = () => {
    if (desktopMenuCloseTimerRef.current === null) return;
    window.clearTimeout(desktopMenuCloseTimerRef.current);
    desktopMenuCloseTimerRef.current = null;
  };

  const openDesktopMenuOnHover = (menu: Exclude<DesktopMenuId, null>) => {
    cancelScheduledDesktopMenuClose();
    if (!pinnedDesktopMenu) {
      setDesktopMenu(menu);
      setShowProfileMenu(false);
      setShowGuestAccountMenu(false);
      setShowLangDropdown(false);
    }
  };

  const scheduleDesktopMenuClose = () => {
    if (pinnedDesktopMenu) return;
    cancelScheduledDesktopMenuClose();
    desktopMenuCloseTimerRef.current = window.setTimeout(() => {
      setDesktopMenu(null);
      desktopMenuCloseTimerRef.current = null;
    }, 350);
  };

  const toggleDesktopMenu = (menu: Exclude<DesktopMenuId, null>) => {
    cancelScheduledDesktopMenuClose();
    if (desktopMenu === menu && pinnedDesktopMenu === menu) {
      setDesktopMenu(null);
      setPinnedDesktopMenu(null);
    } else {
      setDesktopMenu(menu);
      setPinnedDesktopMenu(menu);
    }
    setShowProfileMenu(false);
    setShowGuestAccountMenu(false);
    setShowLangDropdown(false);
  };

  const openDesktopMenuFromKeyboard = (
    menu: Exclude<DesktopMenuId, null>,
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.key !== 'ArrowDown') return;

    event.preventDefault();
    cancelScheduledDesktopMenuClose();
    setDesktopMenu(menu);
    setPinnedDesktopMenu(menu);
    setShowProfileMenu(false);
    setShowGuestAccountMenu(false);
    setShowLangDropdown(false);
    window.requestAnimationFrame(() => {
      const panel = menu === 'exams' ? examsMenuRef.current : menu === 'jurisprudence' ? jurisprudenceMenuRef.current : usefulMenuRef.current;
      panel?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();
    });
  };

  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const baseHeaderClasses = "transition-all duration-300 sticky top-0 z-50 md:h-auto md:min-h-[4rem]";
  const borderClass = isDark ? 'border-b border-border' : (isHomeView ? '' : 'md:border-b border-border/50');
  const bgClass = isDark ? 'bg-surface/90 backdrop-blur-md' : (isHomeView ? 'bg-surface/95 backdrop-blur-md border-b border-border' : 'bg-surface/90 backdrop-blur-md shadow-subtle');
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const showLegislativeTicker = normalizedPath === '/' || normalizedPath === '/cautare';

  return (
    <header className={`ds-site-header ${showLegislativeTicker ? 'search-reference-header' : ''} ${baseHeaderClasses} ${bgClass} ${borderClass} relative`}>
      <div className="ds-header-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 xl:px-8 md:h-auto flex flex-col md:pt-2.5 md:pb-1">

        {/* ═══════════════════════════════════════════════════════════
            MOBILE LAYOUT (hidden on md+) — Premium Redesign
        ════════════════════════════════════════════════════════════ */}
        <div className="search-mobile-header flex md:hidden flex-col" ref={profileMobileRef}>

          <div
            className={`search-mobile-header-top flex flex-col px-4 pt-[14px] pb-1 ${
              isDark ? 'bg-surface border-border' : 'bg-surface'
            } border-b border-border/80`}
            style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}
          >
            {/* Row 1: Logo + Brand + PREMIUM badge + Avatar + Burger */}
            <div className="flex items-center justify-between">

              {/* Left side: Responsive brand logo */}
              <BrandLogo onClick={onReset} full={showLegislativeTicker} />

              {/* Right side: PREMIUM badge + Avatar + Burger */}
              <div className="flex items-center gap-2">

                <div className="search-mobile-controls">
                  <ThemeSelect />
                  {/* Language Switcher Mobile */}
                  <div className="relative" ref={languageMenuMobileRef}>
                    <button
                      onClick={() => setShowLangDropdownMobile(!showLangDropdownMobile)}
                      aria-haspopup="listbox"
                      aria-expanded={showLangDropdownMobile}
                      className={`flex items-center gap-[5px] h-[28px] px-2.5 rounded-full border text-[10px] font-extrabold tracking-wide transition-all duration-200 ${
                        isDark
                          ? 'bg-surface border-border text-content'
                          : 'bg-surface border-border text-content shadow-subtle'
                      }`}
                    >
                      <span
                        className="inline-flex shrink-0 w-[20px] h-[13px] rounded-[2px] overflow-hidden border border-black/[0.07] shadow-subtle"
                        aria-hidden="true"
                      >
                        <FlagIcon code={language} />
                      </span>
                      <span className="uppercase">{language}</span>
                      <ChevronDown size={9} strokeWidth={2.5} className={`transition-transform duration-200 ${showLangDropdownMobile ? 'rotate-180' : ''}`} />
                    </button>

                    {showLangDropdownMobile && (
                      <div
                        role="listbox"
                        className={`absolute left-0 mt-2 w-[152px] rounded-ds-card py-2 z-[70] border animate-fade-in backdrop-blur-xl ${
                          isDark
                            ? 'bg-surface/95 border-border shadow-[0_8px_30px_rgba(0,0,0,0.45)]'
                            : 'bg-surface border-border shadow-[0_8px_30px_rgba(15,23,42,0.11)]'
                        }`}
                      >
                        <div className={`px-3 pb-1.5 pt-0.5 text-[8px] uppercase tracking-[0.15em] font-bold ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          Limbă
                        </div>
                        <div className={`h-px mx-3 mb-1 ${isDark ? 'bg-surface' : 'bg-surface-subtle'}`} />
                        {([
                          { code: 'ro', label: 'Română' },
                          { code: 'en', label: 'English' },
                          { code: 'fr', label: 'Français' },
                        ] as const).map(({ code, label }) => (
                          <button
                            key={code}
                            role="option"
                            aria-selected={language === code}
                            onClick={() => {
                              setLanguage(code);
                              setShowLangDropdownMobile(false);
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-[6px] text-left transition-all duration-150 ${
                              language === code
                                ? isDark ? 'bg-brand/20 text-info' : 'bg-info/10 text-info'
                                : isDark ? 'text-content-muted hover:bg-surface/70' : 'text-content hover:bg-surface-subtle'
                            }`}
                          >
                            <span
                              className="inline-flex shrink-0 w-[22px] h-[14px] rounded-[2px] overflow-hidden shadow-subtle border border-black/[0.08]"
                              aria-hidden="true"
                            >
                              <FlagIcon code={code} />
                            </span>
                            <span className="text-[11px] font-bold">{label}</span>
                            {language === code && (
                              <svg className="ml-auto shrink-0" width="11" height="11" viewBox="0 0 13 13" fill="none">
                                <circle cx="6.5" cy="6.5" r="6.5" className={isDark ? 'fill-blue-500' : 'fill-blue-600'} />
                                <path d="M3.5 6.5L5.5 8.5L9.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </button>
                        ))}
                </div>
              )}
            </div>

                </div>
                {/* PREMIUM badge */}
                {showPremium && <Link
                  to="/abonamente"
                  className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-accent-subtle border border-warning/30 text-warning shadow-subtle transition-all hover:shadow-card active:scale-95 shrink-0"
                >
                  <Crown size={11} strokeWidth={2.5} className="text-warning" />
                  <span>{t('common.premium').toUpperCase()}</span>
                </Link>}

                {/* Avatar / account */}
                {isAuthenticated ? (
                  <button
                    aria-label="Contul meu"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className={`relative flex flex-col items-center justify-center w-9 h-9 rounded-full border-2 transition-all duration-200 active:scale-95 shrink-0 ${
                      isDark ? 'bg-surface border-border' : 'bg-surface-subtle border-border'
                    }`}
                  >
                    <span className="font-bold text-[12px] text-content">{user?.numeComplet?.charAt(0) || t('common.avatarDefault')}</span>
                    <UserTypeBadge role={user?.rol} isDark={isDark} isMobile />
                  </button>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center justify-center w-9 h-9 rounded-full bg-brand text-white border-2 border-info/30 shadow-subtle active:scale-95 transition-all shrink-0"
                  >
                    <LogIn size={14} strokeWidth={2.5} />
                  </Link>
                )}

                {/* Burger Menu Button */}
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className={`flex items-center justify-center w-9 h-9 rounded-full border transition-all duration-300 active:scale-95 shrink-0 ${
                    isDark
                      ? 'bg-surface border-border text-content hover:bg-surface-subtle'
                      : 'bg-background border-border text-content hover:bg-surface-subtle shadow-subtle'
                  }`}
                  aria-label="Meniu principal" aria-expanded={showMobileMenu}
                >
                  {showMobileMenu ? <X size={18} strokeWidth={2.5} /> : <Menu size={18} strokeWidth={2.5} />}
                </button>
              </div>
            </div>

          </div>

          {/* Profile Dropdown (portal-style, below header) */}
          {showProfileMenu && isAuthenticated && (
            <div
              role="menu"
              className="absolute right-4 top-[calc(100%+4px)] w-56 bg-surface rounded-ds-card shadow-overlay py-2 z-50 border border-border animate-fade-in overflow-hidden"
            >
              <div className="px-4 py-3 bg-gradient-to-r from-surface-subtle to-surface border-b border-border">
                <p className="text-xs font-bold text-content truncate">{user?.numeComplet}</p>
                <p className="text-[10px] text-content-muted truncate mt-0.5">{user?.email}</p>
              </div>
              <div className="py-1">
                <button
                  role="menuitem"
                  onClick={() => { toggleDrawer(); setShowProfileMenu(false); }}
                  className="w-full flex items-center px-4 py-2.5 text-xs text-content-secondary hover:bg-surface-subtle hover:text-content transition-colors"
                >
                  <FolderOpen size={14} className="mr-2.5 text-content-muted" />
                  {t('common.cases')}
                  {dosarItems.length > 0 && (
                    <span className="ml-auto bg-danger text-background text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                      {dosarItems.length}
                    </span>
                  )}
                </button>
                <Link
                  role="menuitem"
                  to="/setari"
                  className="flex items-center px-4 py-2.5 text-xs text-content-secondary hover:bg-surface-subtle hover:text-content transition-colors"
                  onClick={() => setShowProfileMenu(false)}
                >
                  <UserIcon size={14} className="mr-2.5 text-content-muted" />
                  {t('common.settings')}
                </Link>
              </div>
              <div className="border-t border-border">
                <button
                  role="menuitem"
                  onClick={() => { logout(); setShowProfileMenu(false); }}
                  className="w-full flex items-center px-4 py-2.5 text-xs text-danger hover:bg-danger/10 transition-colors"
                >
                  <LogOut size={14} className="mr-2.5" />
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}


        </div>

        {/* ══════════════════════════════════════════════════════════════════════════════════
            DESKTOP HEADER (lg+) — Single Row Layout matching media_1787050709180.png
        ══════════════════════════════════════════════════════════════════════════════════ */}
        <div
          ref={desktopMenusRef}
          onClickCapture={(event) => {
            if ((event.target as Element).closest('a[href]')) {
              setDesktopMenu(null);
              setPinnedDesktopMenu(null);
              setShowGuestAccountMenu(false);
            }
          }}
          className="hidden lg:flex items-center justify-between w-full py-2.5"
        >
          {/* Left: Responsive brand; all existing controls retain their positions. */}
          <div className={`brand-header-slot ${!isAuthenticated ? 'brand-header-slot--guest' : ''} flex items-center`}>
            <BrandLogo onClick={onReset} full={showLegislativeTicker} />
          </div>

          {/* Center: Main Navigation Links */}
          <div
            className={`flex items-center ${
              isAuthenticated
                ? 'gap-1.5 xl:gap-3 2xl:gap-4 font-semibold text-xs xl:text-[13px] 2xl:text-[14px]'
                : 'gap-1 lg:gap-1.5 xl:gap-2.5 2xl:gap-4 font-semibold text-[11px] lg:text-[12px] xl:text-[13px] 2xl:text-[14px]'
            }`}
          >
            {/* For guest: PREMIUM is first interactive control after logo */}
            {!isAuthenticated && showPremium && (
              <Link
                to="/abonamente"
                className="flex items-center gap-1 xl:gap-1.5 px-2 xl:px-2.5 2xl:px-3 py-1 xl:py-1.5 rounded-full text-[10px] xl:text-[11px] font-extrabold uppercase tracking-wider bg-accent-subtle border border-warning/30 text-warning hover:bg-warning/20 transition-all shadow-xs shrink-0 whitespace-nowrap"
              >
                <Crown size={12} strokeWidth={2.5} className="text-warning" />
                <span>{t('common.premium')}</span>
              </Link>
            )}

            {/* For guest: Editor DOCX follows immediately after PREMIUM */}
            {!isAuthenticated && (
              <Link
                to={editorDocPath}
                className={`relative flex items-center px-1 lg:px-1.5 xl:px-2 py-2 transition-colors whitespace-nowrap ${
                  isEditorDocActive
                    ? 'text-ai font-bold after:scale-x-100'
                    : `${isDark ? 'text-content-muted' : 'text-content-secondary'} hover:text-ai after:scale-x-0`
                } after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-[2.5px] after:bg-ai after:rounded-full after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200`}
              >
                {t('header.docEditor')}
              </Link>
            )}

            {/* Legislație */}
            <Link
              to="/legislatie"
              className={`relative flex items-center px-1 lg:px-1.5 xl:px-2 py-2 transition-colors whitespace-nowrap ${
                location.pathname.startsWith('/legislatie')
                  ? 'text-ai font-bold after:scale-x-100'
                  : `${isDark ? 'text-content-muted' : 'text-content-secondary'} hover:text-ai after:scale-x-0`
              } after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-[2.5px] after:bg-ai after:rounded-full after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200`}
            >
              {t('header.legislation')}
            </Link>

            {/* Jurisprudență Dropdown */}
            <div
              className="relative flex items-center"
              onMouseEnter={() => openDesktopMenuOnHover('jurisprudence')}
              onMouseLeave={scheduleDesktopMenuClose}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setDesktopMenu(current => current === 'jurisprudence' ? null : current);
                  setPinnedDesktopMenu(current => current === 'jurisprudence' ? null : current);
                }
              }}
            >
              <button
                ref={jurisprudenceButtonRef}
                type="button"
                aria-haspopup="true"
                aria-expanded={desktopMenu === 'jurisprudence'}
                aria-controls="desktop-jurisprudence-menu"
                onClick={() => toggleDesktopMenu('jurisprudence')}
                onKeyDown={(event) => openDesktopMenuFromKeyboard('jurisprudence', event)}
                className={`relative flex items-center gap-0.5 xl:gap-1 px-1 lg:px-1.5 xl:px-2 py-2 transition-colors whitespace-nowrap ${
                  desktopMenu === 'jurisprudence' ||
                  location.pathname.startsWith('/cedo') ||
                  location.pathname.startsWith('/iccj') ||
                  location.pathname.startsWith('/practica-neunitara') ||
                  location.pathname.startsWith('/jurnal-practica-judiciara') ||
                  location.pathname.startsWith('/perspective-juridice')
                    ? 'text-ai font-bold after:scale-x-100'
                    : `${isDark ? 'text-content-muted' : 'text-content-secondary'} hover:text-ai after:scale-x-0`
                } after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-[2.5px] after:bg-ai after:rounded-full after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200`}
              >
                <span>{t('header.jurisprudence')}</span>
                <ChevronDown size={14} strokeWidth={2} className={`transition-transform duration-200 ${desktopMenu === 'jurisprudence' ? 'rotate-180 text-ai' : 'text-content-muted'}`} />
              </button>

              {/* Mega Menu Dropdown Jurisprudență */}
              <div
                ref={jurisprudenceMenuRef}
                id="desktop-jurisprudence-menu"
                role="region"
                aria-label={t('header.jurisprudence')}
                aria-hidden={desktopMenu !== 'jurisprudence'}
                className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[740px] xl:w-[780px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-8.5rem)] overflow-y-auto overscroll-contain shadow-overlay transition-[opacity,transform,visibility] duration-200 border rounded-ds-card ${isDark ? 'bg-surface border-border shadow-slate-900/50' : 'bg-surface border-border'} ${desktopMenu === 'jurisprudence' ? 'opacity-100 visible pointer-events-auto translate-y-1' : 'opacity-0 invisible pointer-events-none translate-y-0 hidden'} z-[60] p-6`}
              >
                <div className="grid grid-cols-3 gap-6 text-left">
                  {/* Column 1: Jurisprudență Națională */}
                  <div className="flex flex-col">
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-4 block`}>
                      {t('header.nationalJurisprudence')}
                    </span>
                    <div className="flex flex-col gap-3">
                      <Link to="/cazuri-asemanatoare" onClick={() => setDesktopMenu(null)} className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content' : 'text-ai'}`}>{t('header.publicCases')}</span>
                        <span className="text-xs mt-0.5 text-content-muted">{t('header.publicCasesDesc')}</span>
                      </Link>
                      <Link to="/iccj" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          ICCJ
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.iccjDesc')}
                        </span>
                      </Link>
                    </div>
                  </div>

                  {/* Column 2: Jurisprudență Europeană */}
                  <div className={`flex flex-col border-l ${isDark ? 'border-border' : 'border-border'} pl-5`}>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-4 block`}>
                      {t('header.europeanJurisprudence')}
                    </span>
                    <div className="flex flex-col gap-3">
                      <Link to="/cedo" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          CEDO
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.cedoDesc')}
                        </span>
                      </Link>
                    </div>
                  </div>

                  {/* Column 3: Practică Judiciară */}
                  <div className={`flex flex-col border-l ${isDark ? 'border-border' : 'border-border'} pl-5`}>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-4 block`}>
                      {t('header.judicialPractice')}
                    </span>
                    <div className="flex flex-col gap-3">
                      <Link to="/practica-neunitara" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.practicaNeunitara')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.practicaNeunitaraDesc')}
                        </span>
                      </Link>
                      <Link to="/jurnal-practica-judiciara" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.jurnalPracticaJudiciara')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.jurnalPracticaJudiciaraDesc')}
                        </span>
                      </Link>
                      <Link to="/perspective-juridice" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.perspectives')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.perspectivesDesc')}
                        </span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Utile Dropdown */}
            <div
              className="relative flex items-center"
              onMouseEnter={() => openDesktopMenuOnHover('useful')}
              onMouseLeave={scheduleDesktopMenuClose}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setDesktopMenu(current => current === 'useful' ? null : current);
                  setPinnedDesktopMenu(current => current === 'useful' ? null : current);
                }
              }}
            >
              <button
                ref={usefulButtonRef}
                type="button"
                aria-haspopup="true"
                aria-expanded={desktopMenu === 'useful'}
                aria-controls="desktop-useful-menu"
                onClick={() => toggleDesktopMenu('useful')}
                onKeyDown={(event) => openDesktopMenuFromKeyboard('useful', event)}
                className={`relative flex items-center gap-0.5 xl:gap-1 px-1 lg:px-1.5 xl:px-2 py-2 transition-colors whitespace-nowrap ${
                  desktopMenu === 'useful' ||
                  location.pathname.startsWith('/utile') ||
                  location.pathname.startsWith('/stiri') ||
                  location.pathname.startsWith('/dictionar') ||
                  location.pathname.startsWith('/crm') ||
                  location.pathname.startsWith('/taxa-timbru') ||
                  location.pathname.startsWith('/calcul-termene') ||
                  location.pathname.startsWith('/calculator-pensie-alimentara') ||
                  location.pathname.startsWith('/calculator-termene-legale') ||
                  location.pathname.startsWith('/calcul-contopiri') ||
                  location.pathname.startsWith('/calcul-limite') ||
                  location.pathname.startsWith('/calcul-prescriptie-penala') ||
                  location.pathname.startsWith('/calcul-reabilitare') ||
                  location.pathname.startsWith('/calcul-salariu-net') ||
                  location.pathname.startsWith('/roi')
                    ? 'text-ai font-bold after:scale-x-100'
                    : `${isDark ? 'text-content-muted' : 'text-content-secondary'} hover:text-ai after:scale-x-0`
                } after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-[2.5px] after:bg-ai after:rounded-full after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200`}
              >
                <span className="whitespace-nowrap">{t('header.useful')}</span>
                <ChevronDown size={14} strokeWidth={2} className={`transition-transform duration-200 ${desktopMenu === 'useful' ? 'rotate-180 text-ai' : 'text-content-muted'}`} />
              </button>

              {/* Mega Menu Dropdown */}
              <div
                ref={usefulMenuRef}
                id="desktop-useful-menu"
                role="region"
                aria-label={t('header.useful')}
                aria-hidden={desktopMenu !== 'useful'}
                className={`absolute top-full left-1/2 -translate-x-[65%] xl:-translate-x-1/2 mt-2 w-[880px] xl:w-[980px] max-w-[calc(100vw-2rem)] max-h-[calc(100vh-8.5rem)] overflow-y-auto overscroll-contain shadow-overlay transition-[opacity,transform,visibility] duration-200 border rounded-ds-card ${isDark ? 'bg-surface border-border shadow-slate-900/50' : 'bg-surface border-border'} ${desktopMenu === 'useful' ? 'opacity-100 visible pointer-events-auto translate-y-1' : 'opacity-0 invisible pointer-events-none translate-y-0 hidden'} z-[60] p-6`}
              >
                <div className="grid grid-cols-4 gap-6 text-left">
                  {/* Column 1: Calculatoare */}
                  <div className="flex flex-col">
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-3 block`}>
                      {t('header.calculators')}
                    </span>
                    <div className="flex flex-col gap-2.5">
                      <Link to="/taxa-timbru" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcTaxaTimbru')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcTaxaTimbruDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-termene" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcTermene')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcTermeneDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-contopiri" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcContopiri')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcContopiriDesc')}
                        </span>
                      </Link>
                      <Link to="/calculator-pensie-alimentara" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcPensieAlimentara')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcPensieAlimentaraDesc')}
                        </span>
                      </Link>
                      <Link to="/calculator-termene-legale" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcTermeneLegale')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcTermeneLegaleDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-limite" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcLimite')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcLimiteDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-prescriptie-penala" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcPrescriptie')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcPrescriptieDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-reabilitare" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcReabilitare')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcReabilitareDesc')}
                        </span>
                      </Link>
                      <Link to="/calcul-salariu-net" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.calcSalariu')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.calcSalariuDesc')}
                        </span>
                      </Link>
                    </div>
                  </div>

                  {/* Column 2: Alte Instrumente */}
                  <div className={`flex flex-col border-l ${isDark ? 'border-border' : 'border-border'} pl-5`}>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-3 block`}>{t('header.otherTools')}</span>
                    <div className="flex flex-col gap-2.5">
                      <Link to="/ghid" className="group/item flex flex-col items-start hover:bg-transparent" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-accent hover:text-accent-hover transition-colors">{t('header.guide')} (45 funcții)</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.guideDesc')}</span>
                      </Link>
                      <Link to="/modificari-legislative" className="group/item flex flex-col items-start hover:bg-transparent" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-ai hover:text-ai/80 transition-colors">{t('header.legislativeChanges')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.legislativeChangesDesc')}</span>
                      </Link>
                      <Link to="/piete-financiare" className="group/item flex flex-col items-start hover:bg-transparent" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-accent hover:text-accent-hover transition-colors">{t('header.financialMarkets')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.financialMarketsDesc')}</span>
                      </Link>
                      <Link to="/stiri" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.legalNews')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.legalNewsDesc')}</span>
                      </Link>
                      <Link to="/dictionar-juridic" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.legalDict')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.legalDictDesc')}</span>
                      </Link>
                      <Link to="/anonimizare-documente" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.docAnonymizer')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.docAnonymizerDesc')}</span>
                      </Link>
                      <Link to="/crm" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.crmPro')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.crmProDesc')}</span>
                      </Link>
                      <Link to="/utile/circumscriptii" className="group/item flex flex-col items-start hover:bg-transparent">
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.competence')}</span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.competenceDesc')}</span>
                      </Link>
                      {settings.extras_carte_funciara_enabled && (
                        <a href="https://www.legeaaplicabila.ro/extras-carte-funciara" target="_blank" rel="noopener noreferrer" className="group/item flex flex-col items-start hover:bg-transparent">
                          <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.extrasCF')}</span>
                          <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.extrasCFDesc')}</span>
                        </a>
                      )}
                      {settings.infiintare_firma_enabled && (
                        <a href="https://www.legeaaplicabila.ro/infiintare-firma-wizard" target="_blank" rel="noopener noreferrer" className="group/item flex flex-col items-start hover:bg-transparent">
                          <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.createCompany')}</span>
                          <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.createCompanyDesc')}</span>
                        </a>
                      )}
                      {isHomeView && (
                        <button
                          onClick={() => {
                            setDesktopMenu(null);
                            setPinnedDesktopMenu(null);
                            onContribuieClick?.();
                          }}
                          className="group/item flex flex-col items-start hover:bg-transparent text-left w-full"
                        >
                          <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>{t('header.addCase')}</span>
                          <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>{t('header.addCaseDesc')}</span>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Column 3: Instrumente Studiu */}
                  <div className={`flex flex-col border-l ${isDark ? 'border-border' : 'border-border'} pl-5`}>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-4 block`}>
                      {t('header.studyTools')}
                    </span>
                    <div className="flex flex-col gap-4">
                      <Link to="/invatare-activa" className="group/item flex flex-col items-start" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-ai dark:text-ai">{t('header.activeLearning')}</span>
                        <span className="text-xs mt-0.5 text-content-muted dark:text-content-muted">{t('header.activeLearningDesc')}</span>
                      </Link>
                      <Link to="/elemente-de-memorat" className="group/item flex flex-col items-start" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-teal-700 dark:text-teal-300">{t('header.memorization')}</span>
                        <span className="text-xs mt-0.5 text-content-muted dark:text-content-muted">{t('header.memorizationDesc')}</span>
                      </Link>
                      <Link to="/scheme-juridice" className="group/item flex flex-col items-start" onClick={() => { setDesktopMenu(null); setPinnedDesktopMenu(null); }}>
                        <span className="text-sm font-bold text-amber-700 dark:text-amber-300">{t('header.legalSchemes')}</span>
                        <span className="text-xs mt-0.5 text-content-muted dark:text-content-muted">{t('header.legalSchemesDesc')}</span>
                      </Link>
                      <a
                        href="https://chat.legeaaplicata.ro/audio-grile"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/item flex flex-col items-start hover:bg-transparent"
                      >
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.audioQuizzes')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.audioQuizzesDesc')}
                        </span>
                      </a>

                      <a
                        href="https://chat.legeaaplicata.ro/audio-legislatie"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/item flex flex-col items-start hover:bg-transparent"
                      >
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.audioLegislation')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.audioLegislationDesc')}
                        </span>
                      </a>

                      <a
                        href="https://chat.legeaaplicata.ro/convertpdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/item flex flex-col items-start hover:bg-transparent"
                      >
                        <span className={`text-sm font-bold ${isDark ? 'text-content group-hover/item:text-accent' : 'text-content group-hover/item:text-ai'} transition-colors`}>
                          {t('header.pdfConverter')}
                        </span>
                        <span className={`text-xs mt-0.5 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                          {t('header.pdfConverterDesc')}
                        </span>
                      </a>
                    </div>
                  </div>

                  {/* Column 4: Investiție în viitor (Featured ROI Card) */}
                  <div className={`flex flex-col border-l ${isDark ? 'border-border' : 'border-border'} pl-5`}>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-content-muted' : 'text-content-muted'} mb-4 block`}>
                      {t('header.futureInvestment')}
                    </span>
                    <Link
                      to="/roi"
                      className="flex flex-col group/card cursor-pointer hover:bg-transparent"
                    >
                      <div className="rounded-xl overflow-hidden mb-3 relative aspect-[16/10] bg-gradient-to-tr from-slate-900 via-blue-900 to-indigo-900 shadow-subtle p-4 flex flex-col justify-end">
                        <div className="absolute inset-0 bg-[url('https://chat.legeaaplicata.ro/assets/prezentare/a.webp')] bg-cover opacity-30 mix-blend-overlay group-hover/card:scale-105 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                        <div className="relative z-10 transition-transform duration-300 group-hover/card:-translate-y-0.5">
                          <span className="text-white text-sm font-bold mb-0.5 block shadow-subtle">{t('header.roiReportTitle')}</span>
                          <span className="text-info text-[11px] block font-medium">{t('header.roiReportSub')}</span>
                        </div>
                      </div>
                      <span className={`text-sm leading-tight font-bold ${isDark ? 'text-content group-hover/card:text-accent' : 'text-content group-hover/card:text-ai'} transition-colors`}>
                        {t('header.roiReportTitle')}
                      </span>
                      <span className={`text-xs leading-snug mt-1 ${isDark ? 'text-content-muted' : 'text-content-muted'}`}>
                        {t('header.roiReportDescText')}
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* For authenticated: Editor DOCX follows after Utile (original position) */}
            {isAuthenticated && (
              <Link
                to={editorDocPath}
                className={`relative flex items-center px-1 lg:px-1.5 xl:px-2 py-2 transition-colors whitespace-nowrap ${
                  isEditorDocActive
                    ? 'text-ai font-bold after:scale-x-100'
                    : `${isDark ? 'text-content-muted' : 'text-content-secondary'} hover:text-ai after:scale-x-0`
                } after:absolute after:bottom-[-2px] after:left-0 after:w-full after:h-[2.5px] after:bg-ai after:rounded-full after:origin-left hover:after:scale-x-100 after:transition-transform after:duration-200`}
              >
                {t('header.docEditor')}
              </Link>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 lg:gap-1.5 xl:gap-3 shrink-0">
            {/* Conditional Premium Button for authenticated user (e.g. Basic role) */}
            {isAuthenticated && showPremium && (
              <Link
                to="/abonamente"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider bg-accent-subtle border border-warning/30 text-warning hover:bg-warning/20 transition-all shadow-xs shrink-0"
              >
                <Crown size={13} strokeWidth={2.5} className="text-warning" />
                <span>{t('common.premium')}</span>
              </Link>
            )}

            {/* Pill C: Dosare + Bell */}
            <div className="ds-header-pill ds-header-case-pill shrink-0">
              <DosarButton
                labelClass={!isAuthenticated ? "hidden xl:inline" : undefined}
                customClass="flex items-center gap-1 xl:gap-2 h-full px-2 xl:px-3 text-xs font-semibold text-content hover:text-ai transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              />
              <div className="ds-header-pill-divider" aria-hidden="true" />
              <Link
                to="/dosare-urmarite"
                aria-label={t('header.followedCases')}
                title={t('header.followedCases')}
                className="flex items-center justify-center h-full px-2 xl:px-2.5 2xl:px-3 text-content-secondary hover:text-ai transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <Bell size={18} strokeWidth={1.8} />
              </Link>
            </div>

            {/* Pill D: Temă + Limbă + Cont */}
            <div className="ds-header-pill ds-header-account-pill relative shrink-0">
              {/* Single Theme Toggle Button */}
              <ThemeSelect />

              <div className="ds-header-pill-divider" aria-hidden="true" />

              {/* Language Selector */}
              <div className="relative h-full flex items-center" ref={languageMenuRef}>
                <button
                  ref={languageButtonRef}
                  type="button"
                  onClick={() => {
                    setShowLangDropdown(!showLangDropdown);
                    setShowProfileMenu(false);
                    setShowGuestAccountMenu(false);
                    setDesktopMenu(null);
                    setPinnedDesktopMenu(null);
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={showLangDropdown}
                  className="flex items-center gap-1 xl:gap-1.5 h-full px-2 xl:px-2.5 text-xs font-bold text-content hover:text-ai transition-colors rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  <span className="inline-flex items-center justify-center w-[18px] h-[13px] rounded-[2px] overflow-hidden border border-black/10">
                    <FlagIcon code={language} />
                  </span>
                  <span className="uppercase text-[11px] font-bold">{language}</span>
                  <ChevronDown size={11} strokeWidth={2.5} className={`transition-transform text-content-muted ${showLangDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Language Dropdown List - 100% Solid Opaque */}
                {showLangDropdown && (
                  <div
                    role="listbox"
                    className={`absolute right-0 top-[calc(100%+8px)] mt-0 w-[172px] rounded-ds-card py-2 z-[100] border animate-fade-in ${
                      isDark
                        ? 'bg-surface border-border shadow-[0_12px_48px_rgba(0,0,0,0.6)]'
                        : 'bg-surface border-border shadow-[0_12px_48px_rgba(15,23,42,0.18)]'
                    }`}
                  >
                    <div className="px-3 pb-1.5 pt-0.5 text-[9px] uppercase tracking-[0.15em] font-bold text-content-muted">
                      Limbă / Language
                    </div>
                    <div className={`h-px mx-3 mb-1.5 ${isDark ? 'bg-surface-subtle' : 'bg-surface-subtle'}`} />
                    {([
                      { code: 'ro', label: 'Română',   sub: 'România' },
                      { code: 'en', label: 'English',  sub: 'United Kingdom' },
                      { code: 'fr', label: 'Français', sub: 'France' },
                    ] as const).map(({ code, label, sub }) => (
                      <button
                        key={code}
                        role="option"
                        aria-selected={language === code}
                        onClick={() => {
                          setLanguage(code);
                          setShowLangDropdown(false);
                        }}
                        className={`w-full flex items-center gap-2.5 px-3 py-[7px] text-left transition-all duration-150 group/opt ${
                          language === code
                            ? isDark
                              ? 'bg-brand/20 text-info'
                              : 'bg-info/10 text-info'
                            : isDark
                            ? 'text-content-muted hover:bg-surface/70'
                            : 'text-content hover:bg-surface-subtle'
                        }`}
                      >
                        <span className="inline-flex shrink-0 w-[26px] h-[17px] rounded-[3px] overflow-hidden shadow-subtle border border-black/[0.08]">
                          <FlagIcon code={code} />
                        </span>
                        <span className="flex flex-col leading-none">
                          <span className="text-[12px] font-bold">{label}</span>
                          <span className="text-[9px] mt-0.5 text-content-muted">{sub}</span>
                        </span>
                        {language === code && (
                          <svg className="ml-auto shrink-0" width="13" height="13" viewBox="0 0 13 13" fill="none">
                            <circle cx="6.5" cy="6.5" r="6.5" className={isDark ? 'fill-blue-500' : 'fill-blue-600'} />
                            <path d="M3.5 6.5L5.5 8.5L9.5 4.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="ds-header-pill-divider" aria-hidden="true" />

              {/* Profile Avatar / Guest Account Dropdown */}
              {isAuthenticated ? (
                <div className="relative h-full flex items-center" ref={profileDesktopRef}>
                  <button
                    ref={profileButtonRef}
                    id="desktop-profile-trigger"
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={showProfileMenu}
                    aria-controls="desktop-profile-menu"
                    className="flex items-center gap-2 h-full pl-2 pr-3.5 rounded-full hover:bg-surface-subtle transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-focus group shrink-0"
                    onClick={() => {
                      setShowProfileMenu(v => !v);
                      setShowLangDropdown(false);
                      setShowGuestAccountMenu(false);
                      setDesktopMenu(null);
                      setPinnedDesktopMenu(null);
                    }}
                  >
                    <div className="relative shrink-0">
                      <div className="w-7 h-7 rounded-full bg-surface-subtle border border-border shadow-xs flex items-center justify-center text-ai overflow-hidden group-hover:border-ai transition-colors">
                        <span className="font-serif font-bold text-xs">{user?.numeComplet?.charAt(0) || t('common.avatarDefault')}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-start leading-tight shrink-0">
                      <span className="text-xs font-bold text-content group-hover:text-ai transition-colors whitespace-nowrap">{user?.numeComplet?.split(' ')[0]}</span>
                      <span className="text-[8px] uppercase tracking-wider font-extrabold text-content-muted whitespace-nowrap">
                        {user?.rol === 'admin' ? 'ADMIN' : (user?.rol || 'UTILIZATOR')}
                      </span>
                    </div>
                  </button>

                  {showProfileMenu && (
                    <div
                      id="desktop-profile-menu"
                      role="menu"
                      aria-labelledby="desktop-profile-trigger"
                      className={`absolute top-[calc(100%+8px)] right-0 mt-0 w-64 max-h-[calc(100vh-8.5rem)] overflow-y-auto overscroll-contain rounded-ds-card shadow-[0_18px_50px_-12px_rgba(15,23,42,0.28)] py-2 z-[70] animate-fade-in border ${isDark ? 'bg-surface border-border' : 'bg-surface border-border'}`}
                    >
                      <div className="px-5 py-3 border-b border-border mb-1">
                        <p className="text-sm font-semibold truncate text-content">{user?.numeComplet}</p>
                        <p className="text-xs truncate mt-0.5 text-content-muted">{user?.email}</p>
                      </div>
                      <Link
                        to="/setari"
                        role="menuitem"
                        className={`flex items-center px-5 py-2.5 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus ${isDark ? 'text-content-muted hover:bg-surface hover:text-white' : 'text-content-secondary hover:bg-surface-subtle hover:text-content'}`}
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <UserIcon size={16} className="mr-3 text-content-muted" />
                        {t('common.settings')}
                      </Link>
                      <div className="border-t border-border my-1"></div>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => { logout(); setShowProfileMenu(false); }}
                        className={`w-full flex items-center px-5 py-2.5 text-sm text-danger transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus ${isDark ? 'hover:bg-danger/40' : 'hover:bg-danger/10'}`}
                      >
                        <LogOut size={16} className="mr-3" />
                        {t('common.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <DesktopGuestAccountMenu
                  isOpen={showGuestAccountMenu}
                  onToggle={() => {
                    setShowGuestAccountMenu(v => !v);
                    setShowLangDropdown(false);
                    setDesktopMenu(null);
                    setPinnedDesktopMenu(null);
                  }}
                  onClose={() => setShowGuestAccountMenu(false)}
                  isDark={isDark}
                  buttonRef={guestAccountButtonRef}
                  menuRef={guestAccountMenuRef}
                />
              )}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════════════════════
            TABLET HEADER (md to <lg) — 2-Row Composition
            ══════════════════════════════════════════════════════════════════════════════════ */}
        <div className="hidden md:flex lg:hidden flex-col w-full">
          <div className="flex items-center justify-between w-full mt-0 pb-1.5">
            <div className="flex items-center gap-4">
              <BrandLogo onClick={onReset} full={showLegislativeTicker} />
            </div>
            <div className="flex items-center gap-2.5 ml-auto">
              {showPremium && <Link to="/abonamente" className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-warning/10 text-warning">
                <Crown size={12} strokeWidth={2} />
                <span>{t('common.premium')}</span>
              </Link>}

              <div className="ds-header-pill ds-header-case-pill shrink-0">
                <DosarButton customClass="flex items-center gap-1.5 h-full px-3 text-xs font-semibold text-content hover:text-ai transition-colors rounded-full" />
                <div className="ds-header-pill-divider" aria-hidden="true" />
                <Link to="/dosare-urmarite" aria-label={t('header.followedCases')} title={t('header.followedCases')} className="flex items-center justify-center h-full px-2.5 text-content-secondary hover:text-ai transition-colors rounded-full">
                  <Bell size={16} strokeWidth={1.8} />
                </Link>
              </div>

              <div className="ds-header-pill ds-header-account-pill relative shrink-0">
                <ThemeSelect />
                <div className="ds-header-pill-divider" aria-hidden="true" />
                {isAuthenticated ? (
                  <Link to="/setari" aria-label="Setări cont" className="flex items-center justify-center h-full px-2.5 text-xs font-bold text-content hover:text-ai transition-colors rounded-full">
                    <span className="w-6 h-6 rounded-full bg-surface-subtle border border-border flex items-center justify-center text-ai text-xs font-bold">{user?.numeComplet?.charAt(0) || 'U'}</span>
                  </Link>
                ) : (
                  <Link to="/login" className="flex items-center gap-1 h-full px-2.5 text-xs font-bold text-ai hover:text-ai-hover transition-colors rounded-full">
                    <LogIn size={14} strokeWidth={2} />
                    <span>{t('common.login')}</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-8 py-1.5 border-t border-border text-xs font-semibold text-content">
            <Link to="/legislatie" className="hover:text-ai">{t('header.legislation')}</Link>
            <Link to="/jurisprudenta" className="hover:text-ai">Jurisprudență</Link>
            <Link to="/cedo" className="hover:text-ai">CEDO</Link>
            <Link to="/iccj" className="hover:text-ai">ICCJ</Link>
          </div>
        </div>
      </div>

      <NavigationDialog open={showMobileMenu} onClose={()=>setShowMobileMenu(false)} className="ds-dialog ds-navigation-drawer">
        <div className="ds-dialog-backdrop" aria-hidden="true" />
        <div className="ds-dialog-position ds-drawer-position">
          <NavigationPanel className="ds-dialog-panel">
            <NavigationTitle className="sr-only">Meniu principal</NavigationTitle>
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border dark:border-border">
          <BrandLogo onClick={() => { onReset?.(); setShowMobileMenu(false); }} />
          <button
            onClick={() => setShowMobileMenu(false)}
            aria-label="Închide meniul"
            className="p-1.5 rounded-full hover:bg-surface-subtle dark:hover:bg-surface text-content-muted dark:text-content-muted transition-colors"
          >
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-5">
          {/* Main Links */}
          <div className="space-y-1">
            <Link
              to="/cautare"
              onClick={() => { onReset?.(); setShowMobileMenu(false); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>{t('common.home')}</span>
            </Link>

            {/* Noua funcție: Editor Documente Word (DOCX) */}
            <Link
              to={editorDocPath}
              onClick={() => { onReset?.(); setShowMobileMenu(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                isEditorDocActive
                  ? 'text-ai bg-ai/10 border border-ai/30'
                  : 'text-ai bg-ai/5 hover:bg-ai/10 border border-ai/20'
              }`}
            >
              <FileText size={16} strokeWidth={2.5} className="text-ai shrink-0" />
              <span>{t('header.docEditor')}</span>
            </Link>

            {/* Admitere Collapsible */}
            <div>
              <button
                onClick={() => setMobileExamsOpen(!mobileExamsOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FolderOpen size={16} strokeWidth={2.5} />
                  <span>{t('header.exams')}</span>
                </div>
                <ChevronDown size={14} className={`transition-transform duration-300 ${mobileExamsOpen ? 'rotate-180 text-accent' : 'text-content-muted'}`} />
              </button>

              {mobileExamsOpen && (
                <div className="pl-9 pr-2 py-1.5 space-y-2 border-l border-border dark:border-border ml-5 mt-1">
                  <Link to="/invatare-activa" onClick={() => setShowMobileMenu(false)} className="block py-1.5 text-xs font-semibold text-ai">{t('header.activeLearning')}</Link>
                  <Link to="/elemente-de-memorat" onClick={() => setShowMobileMenu(false)} className="block py-1.5 text-xs font-semibold text-teal-700">{t('header.memorization')}</Link>
                  <Link to="/scheme-juridice" onClick={() => setShowMobileMenu(false)} className="block py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">{t('header.legalSchemes')}</Link>
                  <a
                    href="https://chat.legeaaplicata.ro/audio-grile"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.audioQuizzes')}
                  </a>
                  <a
                    href="https://chat.legeaaplicata.ro/audio-legislatie"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.audioLegislation')}
                  </a>
                  <a
                    href="https://chat.legeaaplicata.ro/convertpdf"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.pdfConverter')}
                  </a>
                  <Link
                    to="/anonimizare-documente"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-semibold text-ai"
                  >
                    {t('header.docAnonymizer')}
                  </Link>
                </div>
              )}
            </div>

            {/* Legislatie Link */}
            <Link
              to="/legislatie"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1-2.5-2.5Z"/><path d="M6 6h10M6 10h10" />
              </svg>
              <span>{t('header.legislation')}</span>
            </Link>

            {/* CEDO Link */}
            <Link
              to="/cedo"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span>CEDO</span>
            </Link>

            <Link
              to="/practica-neunitara"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <Scale size={16} aria-hidden="true" />
              <span>{t('header.practicaNeunitara')}</span>
            </Link>

            <Link
              to="/jurnal-practica-judiciara"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <BookOpen size={16} aria-hidden="true" />
              <span>{t('header.jurnalPracticaJudiciara')}</span>
            </Link>

            {/* ICCJ Link */}
            <Link
              to="/cazuri-asemanatoare"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-4 py-3 text-ai font-semibold rounded-xl hover:bg-ai/10"
            >
              <Scale size={20} />{t('header.publicCases')}
            </Link>
            <Link
              to="/iccj"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M12 2v20M17 5H7M19 8H5"/>
              </svg>
              <span>ICCJ</span>
            </Link>

            {/* Dosare urmarite Link */}
            <Link
              to="/dosare-urmarite"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <Bell size={16} strokeWidth={2.5} />
              <span>{t('header.followedCases')}</span>
            </Link>

            {/* Utile Collapsible */}
            <div>
              <button
                onClick={() => setMobileUsefulOpen(!mobileUsefulOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Scale size={16} strokeWidth={2.5} />
                  <span className="whitespace-nowrap">{t('header.useful')}</span>
                </div>
                <ChevronDown size={14} className={`transition-transform duration-300 ${mobileUsefulOpen ? 'rotate-180 text-accent' : 'text-content-muted'}`} />
              </button>

              {mobileUsefulOpen && (
                <div className="pl-9 pr-2 py-1.5 space-y-2 border-l border-border dark:border-border ml-5 mt-1">
                  <Link to="/ghid" onClick={() => setShowMobileMenu(false)} className="block py-2 text-xs font-bold text-accent">{t('header.guide')} (45 funcții)</Link>
                  <Link to="/modificari-legislative" onClick={() => setShowMobileMenu(false)} className="block py-1.5 text-xs font-bold text-ai">{t('header.legislativeChangesMobile')}</Link>
                  <Link to="/piete-financiare" onClick={() => setShowMobileMenu(false)} className="block py-1.5 text-xs font-bold text-accent">{t('header.financialMarkets')}</Link>
                  <p className="pt-2 text-[10px] font-bold uppercase tracking-wider text-content-muted">Calculatoare</p>
                  <Link to="/calculator-pensie-alimentara" onClick={() => setShowMobileMenu(false)} className="block py-2 text-xs font-semibold text-ai">{t('header.calcPensieAlimentara')}</Link>
                  <Link to="/calculator-termene-legale" onClick={() => setShowMobileMenu(false)} className="block py-2 text-xs font-semibold text-ai">{t('header.calcTermeneLegale')}</Link>
                  <a
                    href="https://juridiceai.ro/analiza-dosar"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.justPortal')}
                  </a>
                  {settings.extras_carte_funciara_enabled && (
                    <a
                      href="https://www.legeaaplicabila.ro/extras-carte-funciara"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowMobileMenu(false)}
                      className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                    >
                      {t('header.extrasCF')}
                    </a>
                  )}
                  {settings.infiintare_firma_enabled && (
                    <a
                      href="https://www.legeaaplicabila.ro/infiintare-firma-wizard"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setShowMobileMenu(false)}
                      className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                    >
                      {t('header.createCompany')}
                    </a>
                  )}
                  {isHomeView && (
                    <button
                      onClick={() => { onContribuieClick?.(); setShowMobileMenu(false); }}
                      className="block w-full text-left py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai bg-transparent border-0 p-0"
                    >
                      {t('header.addCase')}
                    </button>
                  )}
                  <Link
                    to="/stiri"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.legalNews')}
                  </Link>
                  <Link
                    to="/dictionar-juridic"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.legalDict')}
                  </Link>
                  <Link
                    to="/utile/circumscriptii"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.competence')}
                  </Link>
                  <Link
                    to="/utile/agenda-contacte"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.contacts')}
                  </Link>
                  <Link
                    to="/taxa-timbru"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.taxCalculator')}
                  </Link>
                  <Link
                    to="/roi"
                    onClick={() => setShowMobileMenu(false)}
                    className="block py-1.5 text-xs font-medium text-content-secondary dark:text-content-muted hover:text-ai"
                  >
                    {t('header.roiReport')}
                  </Link>
                </div>
              )}
            </div>

            {/* Perspective Link */}
            <Link
              to="/perspective-juridice"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-content dark:text-content hover:bg-surface-subtle dark:hover:bg-surface transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9h6v6" />
              </svg>
              <span>{t('header.perspectives')}</span>
            </Link>
          </div>

          <div className="pt-2">
            {showPremium && <Link
              to="/abonamente"
              onClick={() => setShowMobileMenu(false)}
              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl text-xs font-black uppercase tracking-wider bg-warning hover:bg-warning text-white shadow-card active:scale-98 transition-all"
            >
              <Crown size={15} strokeWidth={2.5} />
              <span>{t('common.premium').toUpperCase()}</span>
            </Link>}
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="p-5 border-t border-border dark:border-border bg-background dark:bg-surface/50 space-y-3">
          <DosarButton onNavigate={()=>setShowMobileMenu(false)} customClass="w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-surface dark:bg-slate-800 text-xs font-semibold text-content dark:text-slate-300 hover:bg-surface-subtle transition-colors shadow-sm" />

          <div className="flex flex-col items-center text-center">
            <span className="text-[10px] text-content-muted">{t('common.support')} 24/7</span>
            <a
              href="tel:0751661066"
              className="text-sm font-extrabold text-ai dark:text-ai hover:underline mt-0.5"
            >
              📞 0751 661 066
            </a>
          </div>
        </div>
          </NavigationPanel>
        </div>
      </NavigationDialog>
      {showLegislativeTicker && <LegislativeTicker />}
    </header>
  );
};

export default Header;
